import { React, useState, forwardRef, useImperativeHandle, useRef, Typography, useNavigate, useParams, Grid2, CustomTextField, CustomDatePicker, CustomDropdown, CustomMultiSelectDropdown, Box, CustomButton, CustomRadioButton, Divider, SelectChangeEvent, Dayjs, Paper } from "../../utils/globalImports";
import { useApi, useEffect, dayjs, validationMessages, ProposalType, ProposalTypeId, ViewFeeStage, Toaster, ProposalInstructedFeeStage, CustomTableView, ProposalStage } from "../../utils/globalImports";
import { ApiResponse } from '../../services/apiService';
import { validateField, validationPatterns } from "../../utils/validationUtils";

export type GeneralRef = {
    submitForm: () => void;
    approveProposal: () => void;    // Approve Proposal logic
    isDirty: boolean;
};


interface GeneralProps {
    proposalData?: Proposal;
    isProposalLoaded?: boolean;
    onDirtyChange?: (dirty: boolean) => void; //
}

interface Option {
    value: string | number;
    text: string;
}

const isValidDate = (dateString: Dayjs | string) => {
    return dayjs(dateString, 'DD-MM-YYYY', true).isValid();
};

interface Proposal {
    projectNumber?: string;
    projectName?: string;
    projectId: number;
    proposalNumber: string;
    proposalName: string;
    proposalType: string;
    clientId?: number;
    proposalTags: ProposalTags[];
    proposalDueDate: Dayjs | string | null;
    startDate: Dayjs | string | null;
    endDate: Dayjs | string | null;
    proposalStageId: number;
    description: string;
    // clientAssociatedFramework: number,
    // rskAssociatedFramework: number;
    frameworks?: ProposalFramework[];
    isFeeStageExist?: boolean;
    proposalValue?: number;
    confidenceInterval?: number;
    confidenceWeightedValue?: number;
    proposalInstructedValue?: number;
    proposalUninstructedValue?: number;
    status?: number;
    createdId?: number;
    projectStartDate: Dayjs | string | null;
    projectEndDate: Dayjs | string | null;
}

interface ProposalTags {
    proposalId?: number;
    tagId: number;
}

interface ProjectTags {
    projectId?: number;
    tagId: number;
}


interface ProposalFramework {
    proposalId?: number;
    projectId: number;
    clientId: number;
    frameworkId: number;
}

export interface ProposalFormData {
    projectNumber: string;
    projectName: string;
    proposalNumber: string;
    proposalName: string;
    proposalType: number;
    proposalTag: number[];
    proposalDueDate: Dayjs | null;
    startDate: Dayjs | null;
    endDate: Dayjs | null;
    proposalStage: number;
    proposalDescription: string;
    proposalValue: number;
    confidenceInterval: number;
    confidenceWeightedValue: number;
    proposalInstructedValue: number;
    proposalUninstructedValue: number;
    clientAssociatedFramework: number;
    rskAssociatedFramework: number;
    status: boolean;
    projectStartDate: Dayjs | null;
    projectEndDate: Dayjs | null;
}

interface Project {
    projectNumber: string;
    projectName: string;
    projectTags: ProjectTags[];
    frameworks?: ProjectFramework[];
    clientId?: number;
    startDate: Dayjs | null;
    endDate: Dayjs | null;
}

interface ProjectFramework {
    projectId: number;
    clientId: number;
}

interface Framework {
    frameworkId: number;
    frameworkName: string;
    clientId: number;
    status: boolean;
}

const getProposalTypeId = (value: string): ProposalTypeId | undefined =>
    Object.keys(ProposalType).find(key => ProposalType[key as unknown as ProposalTypeId] === value) as ProposalTypeId | undefined;


const cleanNumber = (val: any): number => {
    if (typeof val === 'string') {
        return Number(val.replace(/[^0-9.-]+/g, '')) || 0;
    }
    return Number(val) || 0;
};

const General = forwardRef<GeneralRef, GeneralProps>((props, ref) => {
    const { proposalData } = props;
    const { isProposalLoaded } = props;

    const formRef = useRef<HTMLFormElement>(null);

    const { execute } = useApi();
    const navigate = useNavigate();

    const [isDirty, setIsDirty] = useState(false);
    const [clientId, setClientId] = useState<number>(0);
    const [defaultClientId, setDefaultClientId] = useState<number>(0);
    const [selectedProposal, setSelectedproposal] = useState<Proposal | null>(null);
    const [proposalStageOption, setProposalStageOption] = useState<Option[]>([]);
    const [proposalTagOption, setProposalTagOption] = useState<Option[]>([]);
    const [proposalTypeOption, setProposalTypeOption] = useState<Option[]>([]);
    const [frameworkOption, setFrameworkOption] = useState<Option[]>([]);
    const [rskFrameworkOption, setRSKFrameworkOption] = useState<Option[]>([]);
    const [viewFramework, setViewFramework] = useState({ frameworkId: 0, name: 'Client Associated Framework' });
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [instructedFeeStage, setInstructedFeeStage] = useState<any[]>([]);

    const [instructedModal, setInstructedModal] = useState(false);
    const userId = Number(localStorage.getItem("userId"));
    const role = localStorage.getItem("role");
    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL
    const { proposalId } = useParams<{ proposalId?: string }>(); // Get proposalId from URL


    const [feeStageState, setFeeStageState] = useState({
        exists: false,
        hasZeroOrLessQuantity: false,
    });


    const delay = (ms: number | undefined) => new Promise(resolve => setTimeout(resolve, ms));


    const [formData, setFormData] = useState<ProposalFormData>({
        projectNumber: '',
        projectName: '',
        proposalNumber: '',
        proposalName: '',
        proposalType: -1,
        proposalTag: [],
        proposalDueDate: null,
        startDate: null,
        endDate: null,
        proposalStage: -1,
        proposalDescription: '',
        proposalValue: 0,
        confidenceInterval: 0,
        confidenceWeightedValue: 0,
        proposalInstructedValue: 0,
        proposalUninstructedValue: 0,
        clientAssociatedFramework: -1,
        rskAssociatedFramework: -1,
        status: true,
        projectStartDate: null,
        projectEndDate: null,
    });

    const stage = proposalData?.proposalStageId!;
    const shouldDisableFields =
        (stage >= ProposalStage.SentToClient) ||
        (stage >= ProposalStage.ReadyToReview && role?.toLowerCase() != 'admin');

    const shouldDisableProposalStage = false;
    // (role?.toLowerCase() != 'admin'
    //     && formData.proposalStage >= ProposalStage.ReadyToReview
    //     && formData.proposalStage <= ProposalStage.SentToClient)
    // ;

    const [errors, setErrors] = useState({
        proposalNumber: '',
        proposalName: '',
        proposalType: '',
        proposalDueDate: '',
        startDate: '',
        endDate: '',
        proposalStage: '',
        proposalDescription: '',
        clientAssociatedFramework: '',
        rskAssociatedFramework: '',
        // proposalValue: '',
        confidenceInterval: '',
    });


    // Whenever local dirty state changes, notify parent
    useEffect(() => {
        props.onDirtyChange?.(isDirty);
    }, [isDirty]);

    useEffect(() => {
        if (proposalId) {
            if (!isProposalLoaded) return;
            fetchProposal();
            fetchFeeStages();
            fetchInstructedFeeStages();
        } else {
            fetchProject();
            fetchProposalStage();  // Fetch Proposal Stage on component mount
        }
        setIsDirty(false);
        fetchTags();           // Fetch Proposal Tags on component mount
        fetchProposalType();   // Fetch Proposal Type on component mount
    }, [defaultClientId, proposalId, isProposalLoaded]);


    // fetch Instructed feestage
    const fetchInstructedFeeStages = async () => {
        const { data } = await execute<any[]>('get', `/Proposal/GetInstructedFeeStage/${proposalId}`);
        if (data) {
            const instructedData = data
                .map((feestage) => {
                    const { id, feeStageNumber, description, unitPrice, unitPricePer, instructedValue, poAmount, proposalValue, unInstructedValue, instructionReferences, instructedQuantity } = feestage;
                    return {
                        "Fee Stage": feeStageNumber,
                        description,
                        "Unit Price": ` ${unitPrice.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })} ${unitPricePer}`,
                        "Instructed Quantity": instructedQuantity,
                        "Instructed Value": instructedValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }),
                        "PO Amount": poAmount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }),
                        "Instructed Reference": instructionReferences,
                        "Uninstructed Value": unInstructedValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' }),
                    };
                });

            setInstructedFeeStage(instructedData)
        }
    }


    // fetch feeStages
    const fetchFeeStages = async () => {
        const { data } = await execute('get', `/Proposal/feeStages/${proposalId}/${projectId}`);
        let isfeeStageExist = false;
        if (Array.isArray(data) && data.length > 0) {
            const activeItems = data.filter(item => item.status == 1); // or item?.status === 'active'
            if (activeItems.length > 0) {
                const hasZeroOrLessQuantity = activeItems.some(
                    item => typeof item?.quantity === 'number' && item.quantity <= 0
                );
                isfeeStageExist = true;
                setFeeStageState({
                    exists: true,
                    hasZeroOrLessQuantity,
                });

                const totalProposalValue = activeItems.reduce((sum, row) => {
                    return sum + cleanNumber(row.proposalValue);
                }, 0);
                const totalInstructedValue = activeItems.reduce((sum, row) => {
                    return sum + cleanNumber(row.instructedValue);
                }, 0);

                setFormData(prev => ({
                    ...prev,

                    proposalValue: totalProposalValue,
                    confidenceWeightedValue: totalProposalValue * (formData.confidenceInterval / 100),
                    proposalInstructedValue: totalInstructedValue,
                    proposalUninstructedValue: totalProposalValue - totalInstructedValue,
                }));
            }
            else {
                setFeeStageState({
                    exists: false,
                    hasZeroOrLessQuantity: false,
                });
            }
        }
        fetchProposalStage(isfeeStageExist)
    }


    // fetch project
    const fetchProject = async () => {
        const { data } = await execute<Project>('get', `/Project/GetProject/${projectId}`);
        if (data) {
            setFormData(prev => ({
                ...prev,
                projectNumber: data.projectNumber,
                projectName: data.projectName,
                proposalTag: data.projectTags ? data.projectTags.map(tag => tag.tagId) : [],
                proposalStage: ProposalStage.Draft,
                projectStartDate: data.startDate,
                projectEndDate: data.endDate
            }));

            // const frameworks: ProjectFramework[] = data.frameworks ?? [];
            setClientId(data.clientId!);
            fillFrameworkDropdowns(data.clientId!);
        }
        else {
            navigate('/404');  // Redirect to a Not Found page
        }
    };


    // fetch proposal
    const fetchProposal = async () => {
        // const { data } = await execute<Proposal>('get', `/Proposal/proposals/${proposalId}/details`);
        if (proposalData) {
            setSelectedproposal(proposalData)
            const clientFramework = proposalData.frameworks?.find(tag => tag.clientId === proposalData.clientId);
            const rskFramework = proposalData.frameworks?.find(tag => tag.clientId !== proposalData.clientId);
            // fillFrameworkDropdowns(clientFramework?.clientId!);
            if (clientFramework?.clientId) {
                fillFrameworkDropdowns(clientFramework.clientId,false);
            }
            else if (rskFramework?.clientId) {
                fillFrameworkDropdowns(rskFramework.clientId,true);
            }
            setClientId(clientFramework?.clientId!)

            const clientAssociatedFrameworkId = clientFramework?.frameworkId ?? -1;
            const selectedOption = frameworkOption.find(opt => opt.value === clientAssociatedFrameworkId);

            // setViewFramework({
            //     frameworkId: clientAssociatedFrameworkId,
            //     name: selectedOption ? selectedOption.text : 'Client Associated Framework'
            // });

            // Update formData with fetched data
            setFormData(prev => ({
                ...prev,

                projectNumber: proposalData.projectNumber!,
                projectName: proposalData.projectName!,
                proposalNumber: proposalData.proposalNumber || '',
                proposalName: proposalData.proposalName || '',
                proposalType: getProposalTypeId(proposalData.proposalType) ?? -1,

                startDate: proposalData.startDate ? dayjs(proposalData.startDate) : null,
                endDate: proposalData.endDate ? dayjs(proposalData.endDate) : null,
                proposalDueDate: proposalData.proposalDueDate ? dayjs(proposalData.proposalDueDate) : null,
                proposalTag: proposalData.proposalTags ? proposalData.proposalTags.map(tag => tag.tagId) : [],
                proposalStage: proposalData.proposalStageId || -1,
                proposalDescription: proposalData.description || '',
                status: proposalData.status ? true : false,

                clientAssociatedFramework: clientAssociatedFrameworkId,
                rskAssociatedFramework: rskFramework?.frameworkId ?? -1,
                confidenceInterval: proposalData.confidenceInterval!,
                projectStartDate: proposalData.startDate ? dayjs(proposalData.startDate) : null,
                projectEndDate: proposalData.endDate ? dayjs(proposalData.endDate) : null,
            }));
        }
    };

    // fetch proposalTags
    const fetchProposalStage = async (isfeeStageExist?: boolean) => {

        const { data } = await execute('get', '/Proposal/stages');
        if (data && Array.isArray(data)) {

            let allowOrder: ProposalStage[] = [ProposalStage.Draft, ProposalStage.NotRequired];

            const currentStage = proposalData?.proposalStageId;

            // check if the proposal is approved/signed
            if (currentStage === ProposalStage.Signed) {
                if (role?.toLowerCase() !== 'admin') {
                    allowOrder = [
                        ProposalStage.Signed,
                        ProposalStage.SentToClient
                    ];
                }
                else {
                    allowOrder.push(
                        ProposalStage.ReadyToReview,
                        ProposalStage.Signed,
                        ProposalStage.SentToClient
                    )
                }
            }
            else if (currentStage == ProposalStage.SentToClient) {
                allowOrder = [
                    ProposalStage.SentToClient,
                    ProposalStage.Instructed
                ]
            }
            else if (currentStage == ProposalStage.Instructed) {
                allowOrder = [
                    ProposalStage.Instructed,
                    ProposalStage.Invoiced,
                    ProposalStage.Paid,
                    ProposalStage.Closed
                ]
            }
            else if (currentStage == ProposalStage.Invoiced) {
                allowOrder = [
                    ProposalStage.Invoiced,
                    ProposalStage.Paid,
                    ProposalStage.Closed
                ]
            }
            else if (currentStage == ProposalStage.Paid) {
                allowOrder = [
                    ProposalStage.Paid,
                    ProposalStage.Closed
                ]
            }
            else if (currentStage == ProposalStage.Closed) {
                allowOrder = [
                    ProposalStage.Closed
                ]
            }
            else if (proposalData?.isFeeStageExist || isfeeStageExist) { // check if the proposal has a feestage
                allowOrder.push(
                    ProposalStage.ReadyToReview
                )
            }

            const filteredStages = data.filter(proposalstage =>
                allowOrder.includes(proposalstage.stageOrder) && proposalstage.status
            );

            const proposalstageOptions = filteredStages
                .map(proposalstage => ({
                    value: proposalstage.proposalStageId,
                    text: proposalstage.proposalStageName
                }))

            setProposalStageOption(proposalstageOptions);
        }
    };

    const fetchFrameworks = async (clientId: number) => {
        const { data } = await execute('get', `/Framework/GetFrameworkByClientId/${clientId}`);
        const stageId = proposalData?.proposalStageId;
        if (data && Array.isArray(data)) {
            if (stageId === 5) {
                return data;
            }
            return data.filter(framework => framework.status); // Filter for active framework
        }

    }

    const fillFrameworkDropdowns = async (clienId: number,isDefault:Boolean = false) => {
        const data = await fetchFrameworks(clienId);
        if (data) {
            const defaultFramework = data.filter(x => isDefault ? (x.clientId == clienId) :(x.clientId !== clienId));
             
            if (defaultFramework.length > 0) {
                const defaultOptions = defaultFramework
                    .map(framework => ({
                        value: framework.frameworkId,
                        text: framework.frameworkName
                    }))
                    .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));

                setRSKFrameworkOption(defaultOptions)
                setDefaultClientId(defaultFramework[0].clientId)
                setFormData(prev => ({
                    ...prev,
                    rskAssociatedFramework: defaultOptions?.[0]?.value || -1,
                }));
            }

            const clientFramework = data.filter(x =>isDefault? (x.clientId !== clienId):(x.clientId === clienId));
            if (clientFramework.length > 0) {
                const clientOptions = clientFramework
                    .map(framework => ({
                        value: framework.frameworkId,
                        text: framework.frameworkName
                    }))
                    .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })); // Case-insensitive sorting;;
                setFrameworkOption(clientOptions);
                setFormData(prev => ({
                    ...prev,
                    clientAssociatedFramework: clientOptions?.[0]?.value || -1,
                }));
            }
        }
    }

    // fetch proposalTags
    const fetchTags = async () => {
        const { data } = await execute('get', '/Lookup/GetAllProposalTags');
        if (data && Array.isArray(data)) {
            const tagsOptions = data
                .filter(tag => tag.status) // Filter for active tags
                .map(tag => ({
                    value: tag.proposalTagId,
                    text: tag.proposalTag
                }))
                .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })); // Case-insensitive sorting;;
            setProposalTagOption(tagsOptions);
        }
    };

    // fetch ProposalType
    const fetchProposalType = async () => {
        const proposalTypeOptions = Object.entries(ProposalType).map(([id, description]) => ({
            value: id,  // Ensure `id` exists in the response
            text: description, // Ensure `name` exists in the response
        }))
            .sort((a, b) => a.text.localeCompare(b.text));
        setProposalTypeOption(proposalTypeOptions);
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : type === "radio" ? value === "true" : value,
        }));
        setIsDirty(true);
    };

    // validate proposal form field
    const validate = () => {
        let isValid = true;
        const errors = { proposalNumber: '', proposalName: '', proposalType: '', proposalDueDate: '', startDate: '', endDate: '', proposalStage: '', proposalDescription: '', clientAssociatedFramework: '', rskAssociatedFramework: '', confidenceInterval: '' };

        const pattern = `${formData.projectNumber}/`;
        if (proposalId && !formData.proposalNumber) {
            errors.proposalNumber = validationMessages.common_required("Proposal number");
            isValid = false;
        }
        else if (formData.proposalNumber.length > 20) {
            errors.proposalNumber = validationMessages.common_maxLength("Proposal number", 20);
            isValid = false;
        }
        else if (proposalId && !formData.proposalNumber.startsWith(pattern)) {
            errors.proposalNumber = validationMessages.invalid_proposal_Number;
            isValid = false;
        }

        errors.proposalName = validateField(formData.proposalName, {
            fieldName: "Proposal Name",
            required: true,
            maxLength: 100,
        });

        if (errors.proposalName) isValid = false;

        // Validate dropdowns
        errors.proposalType = validateField(formData.proposalType, {
            fieldName: "Proposal Type",
            required: true
        });
        if (errors.proposalType) isValid = false;

        if (!formData.proposalDueDate) {
            errors.proposalDueDate = validationMessages.common_required("Proposal due date");
            isValid = false;
        }
        else if (!isValidDate(formData.proposalDueDate)) {
            errors.proposalDueDate = validationMessages.common_invalidDate("Proposal due date");
            isValid = false;
        }

        if (!formData.startDate) {
            errors.startDate = validationMessages.common_required("Start date");
            isValid = false;
        }
        else if (!isValidDate(formData.startDate)) {
            errors.startDate = validationMessages.common_invalidDate("Start date");
            isValid = false;
        }


        if (!formData.endDate) {
            errors.endDate = validationMessages.common_required("End date");
            isValid = false;
        }
        else if (!isValidDate(formData.endDate)) {
            errors.endDate = validationMessages.common_invalidDate("End date");
            isValid = false;
        }
        else if (
            formData.startDate &&
            formData.endDate &&
            (dayjs(formData.endDate).isSame(dayjs(formData.startDate), 'day') ||
                dayjs(formData.endDate).isBefore(dayjs(formData.startDate), 'day'))
        ) {
            errors.endDate = validationMessages.framework_endDate;
            isValid = false;
        }

        if (formData.proposalStage == -1) {
            errors.proposalStage = validationMessages.common_required("Proposal Stage");
            isValid = false;
        }
        else if (formData.proposalStage == ProposalStage.ReadyToReview && feeStageState.hasZeroOrLessQuantity) {
            errors.proposalStage = validationMessages.proposal_General_proposalType;
            isValid = false;
        }

        errors.proposalDescription = validateField(formData.proposalDescription, {
            fieldName: "Description",
            maxLength: 5000
        });

        if (errors.proposalDescription) isValid = false;

        if (formData.clientAssociatedFramework == -1 && frameworkOption.length > 0) {
            errors.clientAssociatedFramework = validationMessages.common_required("Client Associated Framework");
            isValid = false;
        }

        errors.rskAssociatedFramework = validateField(formData.rskAssociatedFramework, {
            fieldName: "RSK BSL Associated Framework",
            required: true
        });

        if (errors.rskAssociatedFramework) isValid = false;

        if (formData.projectStartDate && formData.startDate?.isBefore(formData.projectStartDate, "day")) {
            errors.startDate = "Start date cannot be before project start";
            isValid = false;
        }

        if (formData.projectEndDate && formData.endDate?.isAfter(formData.projectEndDate, "day")) {
            errors.endDate = "End date cannot be after project end";
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    // Expose method to parent
    useImperativeHandle(ref, () => ({
        submitForm: handleSubmit,
        approveProposal: handleApproveProposal,
        isDirty,
    }));

    const handleNavigate = async (shouldDelay = false) => {
        if (shouldDelay) {
            await delay(1500); // Apply delay for 2 seconds if shouldDelay is true
        }
        navigate(`/proposal/proposals/${projectId}/${formData.projectName}`)
    };

    const handleSubmit = async () => {
        if (validate()) {
            if (formRef.current) {
                let response: ApiResponse<any>;

                const proposalTags: ProposalTags[] = formData.proposalTag.map((tagId: number) => ({
                    tagId
                }));

                if (proposalId) {

                    const AssociatedFramework: ProposalFramework[] = frameworkOption.length > 0 && formData.clientAssociatedFramework
                        ? [{
                            proposalId: Number(proposalId),
                            projectId: Number(projectId),
                            clientId: clientId,
                            frameworkId: formData.clientAssociatedFramework,
                        }]
                        : [];

                    const updatedProposal = {
                        ...selectedProposal, // Keep the original user data

                        proposalNumber: formData.proposalNumber,
                        proposalName: formData.proposalName,
                        proposalType: ProposalType[formData.proposalType as ProposalTypeId],
                        proposalTags: proposalTags,
                        proposalDueDate: formData.proposalDueDate ? dayjs(formData.proposalDueDate).format("YYYY-MM-DD") : null,
                        startDate: formData.startDate ? dayjs(formData.startDate).format("YYYY-MM-DD") : null,
                        endDate: formData.endDate ? dayjs(formData.endDate).format("YYYY-MM-DD") : null,
                        proposalStageId: formData.proposalStage,
                        description: formData.proposalDescription,
                        frameworks: AssociatedFramework,
                        updatedId: userId,
                        status: formData.status ? 1 : 0,
                        confidenceInterval: formData.confidenceInterval
                    };

                    response = await execute('put', `/Proposal/proposals/${proposalId}`, updatedProposal);
                }
                else {
                    const AssociatedFramework: ProposalFramework[] = [
                        {
                            projectId: Number(projectId),
                            clientId: defaultClientId,
                            frameworkId: formData.rskAssociatedFramework,
                        },
                        ...(frameworkOption.length > 0 && formData.clientAssociatedFramework
                            ? [{
                                projectId: Number(projectId),
                                clientId: clientId,
                                frameworkId: formData.clientAssociatedFramework,
                            }]
                            : []),
                    ];

                    // Add the Proposal
                    const newProposal: Proposal = {
                        projectId: Number(projectId),
                        proposalNumber: formData.proposalNumber,
                        proposalName: formData.proposalName,
                        proposalType: ProposalType[formData.proposalType as ProposalTypeId],
                        proposalTags: proposalTags,
                        proposalDueDate: formData.proposalDueDate ? dayjs(formData.proposalDueDate).format("YYYY-MM-DD") : null,
                        startDate: formData.startDate ? dayjs(formData.startDate).format("YYYY-MM-DD") : null,
                        endDate: formData.endDate ? dayjs(formData.endDate).format("YYYY-MM-DD") : null,
                        proposalStageId: formData.proposalStage,
                        description: formData.proposalDescription,
                        frameworks: AssociatedFramework,
                        createdId: userId,
                        status: formData.status ? 1 : 0,
                        projectStartDate: formData.projectStartDate ? dayjs(formData.projectStartDate).format("YYYY-MM-DD") : null,
                        projectEndDate: formData.projectEndDate ? dayjs(formData.projectEndDate).format("YYYY-MM-DD") : null,
                    };

                    // console.log(newProposal)
                    response = await execute('post', '/Proposal/proposals', newProposal);
                }

                if (response!) {
                    if (response.error) {
                        // If there's an error in the response, display the error message
                        setToast({ open: true, message: response.error, type: "error" });
                    } else {
                        setIsDirty(false);
                        if (typeof response.data === 'object' && response.data.proposalId) {
                            // If the response is successful, display the success message
                            setToast({ open: true, message: validationMessages.proposal_InsertResponse_Text, type: "success" });
                            navigate(`/proposal/add-proposal/${projectId}/${response.data.proposalId}/${formData.projectName}`);
                        }
                        else {
                            setToast({ open: true, message: response.data, type: "success" });
                            // handleNavigate(true);
                        }
                    }
                }
                formRef.current.requestSubmit(); // Native form submission
            }
        }
    };

    const handleApproveProposal = async () => {
        if (validate()) {
            if (formRef.current) {
                try {
                    // Prepare the API request data
                    let response: ApiResponse<any>;

                    const proposalStageSigned = 4 // stage Signed
                    // Create the updated proposal object
                    const approvedProposal = {
                        ...selectedProposal, // Keep the original data
                        proposalStageId: proposalStageSigned,
                        updatedId: userId,
                    };

                    // console.log(approvedProposal)
                    response = await execute('put', `/Proposal/proposals/${proposalId}`, approvedProposal);

                    if (response!) {
                        if (response.error) {
                            // If there's an error in the response, display the error message
                            setToast({ open: true, message: response.error, type: "error" });
                        } else {
                            setIsDirty(false);
                            setToast({ open: true, message: response.data, type: "success" });
                            //handleNavigate(true);
                        }
                    }

                    // Submit the form after the API call if needed
                    formRef.current.requestSubmit(); // Native form submission
                } catch (error) {

                }
            }
        }
    };


    return (

        <form ref={formRef}>
            <Box sx={{ flexGrow: 1 }}>
                <Paper elevation={0} sx={{border: '1px solid #e0eaf6', borderRadius: '14px', overflow: 'hidden', mb: 2}}>
                    <Typography sx={{ fontSize: "25px",  color: "#1e66ab", fontWeight: 600, ml: 0, display: 'flex', px: 2.5, py: 1.5, backgroundColor: '#f0f6ff', borderBottom: '1px solid #e0eaf6' }}>
                        Details:
                    </Typography>

                    <Grid2 container spacing={2} sx={{px: 2.5, py: 1.5, backgroundColor: '#f7faff' }}>
                        <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                            <CustomTextField
                                label="Project Number"
                                name="projectNumber"
                                value={formData.projectNumber}
                                onChange={handleChange}
                                sx={{ mb: 1,backgroundColor: '#fff' }}
                                disable
                            />
                        </Grid2>

                        {/* <Grid2 size={{ xs: 12, md: 6 }}>
                        <CustomTextField
                            label="Project Name"
                            name="projectName"
                            value={formData.projectName}
                            onChange={handleChange}
                            sx={{ mb: 1 }}
                            disable
                        />
                    </Grid2> */}

                        <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                            <CustomTextField
                                label="Proposal Number"
                                name="proposalNumber"
                                value={formData.proposalNumber}
                                onChange={handleChange}
                                sx={{ mb: 1, backgroundColor: '#fff' }}
                                disable={!proposalId || shouldDisableFields}
                            />
                            {errors.proposalNumber && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.proposalNumber}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CustomTextField
                                label="Proposal Name"
                                name="proposalName"
                                value={formData.proposalName}
                                onChange={handleChange}
                                sx={{ mb: 1, backgroundColor: '#fff' }}
                                required={true}
                                disable={shouldDisableFields}
                            />
                            {errors.proposalName && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.proposalName}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CustomDropdown
                                value={formData.proposalType}
                                onChange={(event) => {
                                    setFormData({ ...formData, proposalType: Number(event.target.value) })
                                    setIsDirty(true);
                                }}
                                label="Proposal Type"
                                options={proposalTypeOption}
                                requiredLabel={true}
                                sx={{ mb: 1, height: '35px', backgroundColor: '#fff'}}
                                disable={shouldDisableFields}
                            />
                            {errors.proposalType && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.proposalType}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            {/* <CustomDropdown
                        value={formData.proposalTag}
                        onChange={(event) => setFormData({ ...formData, proposalTag: Number(event.target.value) })}
                        label="Proposal Tag"
                        requiredLabel={true}
                        required={false}
                        options={proposalTags}
                        sx={{ mb: 1, height: '35px', }}
                    /> */}


                            <CustomMultiSelectDropdown
                                selectedValues={formData.proposalTag}
                                onChange={(event) => {
                                    setFormData({ ...formData, proposalTag: event.map(Number) })
                                    setIsDirty(true);
                                }}
                                label="Proposal Tag"
                                options={proposalTagOption}
                                requiredLabel
                                required={false}
                                sx={{ height: '35px' }}
                                disable={shouldDisableFields}
                            />

                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CustomDatePicker
                                label="Start Date"
                                value={formData.startDate}
                                required={true}
                                onChange={(newValue) => {
                                    if (!newValue) {
                                        setFormData({ ...formData, startDate: null });
                                        setErrors(prev => ({ ...prev, startDate: "Start date is required" }));
                                        return;
                                    }

                                    // ✅ Validate immediately on change
                                    const isBefore = formData.projectStartDate && newValue.isBefore(formData.projectStartDate, "day");
                                    const isAfter = formData.projectEndDate && newValue.isAfter(formData.projectEndDate, "day");

                                    if (isBefore || isAfter) {
                                        setErrors(prev => ({
                                            ...prev,
                                            startDate: "Start date must be between project start and project end",
                                        }));
                                        setFormData({ ...formData, startDate: newValue }); // still store the value so input doesn’t clear
                                    } else {
                                        setErrors(prev => ({ ...prev, startDate: "" })); // clear error
                                        setFormData({ ...formData, startDate: newValue });
                                    }

                                    setIsDirty(true);
                                }}
                                sx={{ mb: 1, height: '35px',backgroundColor: '#fff' }}
                                disable={shouldDisableFields}
                                shouldDisableDate={(date) => {
                                    const disableBefore = formData.projectStartDate ? date.isBefore(formData.projectStartDate, "day") : false;
                                    const disableAfter = formData.projectEndDate ? date.isAfter(formData.projectEndDate, "day") : false;
                                    return disableBefore || disableAfter;
                                }}
                            />

                            {errors.startDate && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.startDate}
                                </Typography>
                            )}

                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CustomDatePicker
                                label="End Date"
                                value={formData.endDate}
                                required={true}
                                onChange={(newValue) => {
                                    if (!newValue) {
                                        setFormData({ ...formData, endDate: null });
                                        setErrors(prev => ({ ...prev, endDate: "End date is required" }));
                                        return;
                                    }

                                    // ✅ Validate immediately on change
                                    const isBefore = formData.projectStartDate && newValue.isBefore(formData.projectStartDate, "day");
                                    const isAfter = formData.projectEndDate && newValue.isAfter(formData.projectEndDate, "day");

                                    if (isBefore || isAfter) {
                                        setErrors(prev => ({
                                            ...prev,
                                            endDate: "End date must be between project start date and  end date",
                                        }));
                                        setFormData({ ...formData, endDate: newValue }); // still store the value so input doesn’t clear
                                    } else {
                                        setErrors(prev => ({ ...prev, endDate: "" })); // clear error
                                        setFormData({ ...formData, endDate: newValue });
                                    }

                                    setIsDirty(true);
                                }}
                                sx={{ mb: 1, height: '35px', backgroundColor: '#fff' }}
                                disable={shouldDisableFields}
                                shouldDisableDate={(date) => {
                                    const disableBefore = formData.projectStartDate ? date.isBefore(formData.projectStartDate, "day") : false;
                                    const disableAfter = formData.projectEndDate ? date.isAfter(formData.projectEndDate, "day") : false;
                                    return disableBefore || disableAfter;
                                }}
                            />
                            {errors.endDate && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.endDate}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CustomDatePicker
                                label="Proposal due date"
                                value={formData.proposalDueDate}
                                required={true}
                                onChange={(newValue) => {
                                    if (!newValue) {
                                        setFormData({ ...formData, proposalDueDate: null });
                                        setErrors(prev => ({ ...prev, proposalDueDate: "End date is required" }));
                                        return;
                                    }
                                    const isBefore = formData.startDate && newValue.isBefore(formData.startDate, "day");
                                    const isAfter = formData.endDate && newValue.isAfter(formData.endDate, "day");

                                    if (isBefore || isAfter) {
                                        setErrors(prev => ({
                                            ...prev,
                                            proposalDueDate: "Proposal Due date must be between proposal start and proposal end",
                                        }));
                                        setFormData({ ...formData, proposalDueDate: newValue }); // still store the value so input doesn’t clear
                                    } else {
                                        setErrors(prev => ({ ...prev, proposalDueDate: "" })); // clear error
                                        setFormData({ ...formData, proposalDueDate: newValue });
                                    }

                                    setIsDirty(true);
                                }}
                                sx={{ mb: 1, height: '35px', backgroundColor: '#fff' }}
                                disable={shouldDisableFields}
                                shouldDisableDate={(date) => {
                                    const disableBefore = formData.startDate ? date.isBefore(formData.startDate, "day") : false;
                                    const disableAfter = formData.endDate ? date.isAfter(formData.endDate, "day") : false;
                                    return disableBefore || disableAfter;
                                }}

                            />
                            {errors.proposalDueDate && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.proposalDueDate}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CustomDropdown
                                value={
                                    formData.proposalStage === null ||
                                        formData.proposalStage === undefined ||
                                        formData.proposalStage === -1
                                        ? "-1"
                                        : formData.proposalStage
                                }
                                onChange={(event) => {
                                    const selectedValue = Number(event.target.value);

                                    // If user tries to pick the placeholder ("Select Proposal Stage"), ignore it
                                    if (selectedValue === -1) return;

                                    setFormData({
                                        ...formData,
                                        proposalStage: selectedValue,
                                    });
                                    setIsDirty(true);
                                }}
                                label="Proposal Stage"
                                options={
                                    proposalStageOption && proposalStageOption.length > 0
                                        ? proposalStageOption.filter(opt => opt.text !== "Proposal Stage")
                                        : []
                                }
                                requiredLabel={true}
                                sx={{ mb: 1, height: "35px", backgroundColor: '#fff' }}
                                disable={shouldDisableProposalStage}
                            />
                            {errors.proposalStage && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.proposalStage}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CustomTextField
                                label="Proposal Description"
                                name="proposalDescription"
                                value={formData.proposalDescription}
                                onChange={handleChange}
                                disable={shouldDisableFields}
                                sx={{
                                    mb: 1,
                                    backgroundColor: '#fff'
                                }}
                                isTextArea={true}
                            />
                        </Grid2>

                        {proposalId && (
                            <>
                                <Grid2 size={{ xs: 6, md: 3, lg: 3 }}>
                                    <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: -2.5}}>Status</Typography>
                                    <Grid2 sx={{ display: 'flex' }}> {/* Adjust gap as needed */}
                                        {/* Custom Radio Buttons */}
                                        <CustomRadioButton
                                            label="Active"
                                            value="true"
                                            name="status"
                                            checked={formData.status === true}
                                            onChange={handleChange}
                                            required={true}
                                            disabled={shouldDisableFields}
                                        />
                                        <CustomRadioButton
                                            label="Inactive"
                                            value="false"
                                            name="status"
                                            checked={formData.status === false}
                                            onChange={handleChange}
                                            required={true}
                                            disabled={shouldDisableFields}
                                        />
                                    </Grid2>
                                </Grid2>
                            </>
                        )}

                    </Grid2>
                </Paper>



                {stage >= ProposalStage.SentToClient && (
                    <>

                        <Grid2 container spacing={1} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb:2 }}>
                            <Grid2 size={{ xs: 12, md: 3, lg: 2 }}>
                                <CustomButton
                                    capsule
                                    sx={{
                                        mt: 0,
                                        mb: 2,
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap',
                                    }}
                                    type="button"
                                    backgroundColor="#1e66ab"
                                    hoverColor="rgba(150, 193, 33, 0.75)"
                                    onClick={() => {
                                        setInstructedModal(true)
                                    }} // Trigger search when clicked
                                >
                                    Add Instructed Fee stages
                                </CustomButton>

                                <ProposalInstructedFeeStage
                                    open={instructedModal}
                                    onClose={() => setInstructedModal(false)}
                                    afterSubmit={() => fetchInstructedFeeStages()}
                                >

                                </ProposalInstructedFeeStage>
                            </Grid2>

                            {instructedFeeStage.length > 0 && (
                                <CustomTableView data={instructedFeeStage}></CustomTableView>
                            )}

                        </Grid2>
                    </>
                )}


                <Paper elevation={0} sx={{border: '1px solid #e0eaf6', borderRadius: '14px', overflow: 'hidden', mb: 2}}>
                    <Typography sx={{ fontSize: "25px",  color: "#1e66ab", fontWeight: 600, ml: 0, display: 'flex', px: 2.5, py: 1.5, backgroundColor: '#f0f6ff', borderBottom: '1px solid #e0eaf6' }}>
                        Framework
                    </Typography>
                    {frameworkOption.length > 0 && (
                        <Grid2 container spacing={2} sx={{px: 2.5, py: 1.5, backgroundColor: '#f7faff' }}>
                            <Grid2 size={{ xs: 12, md: 6 }}>
                                <CustomDropdown
                                    value={formData.clientAssociatedFramework}
                                    onChange={(event) => {
                                        const selectedValue = Number(event.target.value);
                                        const selectedOption = frameworkOption.find(opt => opt.value === selectedValue);

                                        setFormData({
                                            ...formData,
                                            clientAssociatedFramework: selectedOption ? selectedValue : -1
                                        });
                                        setIsDirty(true);

                                        // setViewFramework({
                                        //     frameworkId: selectedOption ? selectedValue : 0,
                                        //     name: selectedOption ? selectedOption.text : 'Client Associated Framework'
                                        // });
                                    }}
                                    // onChange={(event) => setFormData({ ...formData, clientAssociatedFramework: Number(event.target.value) })}
                                    label="Select Client Associated Framework"
                                    requiredLabel={true}
                                    required={true}
                                    disable={proposalData?.isFeeStageExist || feeStageState.exists}
                                    options={frameworkOption}
                                    sx={{ mb: 1, height: '35px',backgroundColor: '#fff' }}
                                />
                                {errors.clientAssociatedFramework && (
                                    <Typography className="validationMessage" role="alert">
                                        {errors.clientAssociatedFramework}
                                    </Typography>
                                )}
                            </Grid2>
                            <ViewFeeStage frameworkId={formData.clientAssociatedFramework}
                                name={frameworkOption.find(
                                    (opt) => opt.value === formData.clientAssociatedFramework
                                )?.text || "Client Associated Framework"}>

                            </ViewFeeStage>
                        </Grid2>
                    )}

                    <Grid2 container spacing={2} sx={{px: 2.5, py: 1.5, backgroundColor: '#f7faff' }}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <CustomDropdown
                                value={formData.rskAssociatedFramework}
                                onChange={(event) => {
                                    setFormData({
                                        ...formData,
                                        rskAssociatedFramework: Number(event.target.value)
                                    })
                                    setIsDirty(true);
                                }}
                                label="Select RSK BSL Associated Framework"
                                requiredLabel={true}
                                required={true}
                                disable={proposalData?.isFeeStageExist || feeStageState.exists}
                                options={rskFrameworkOption}
                                sx={{ mb: 1, height: '35px',backgroundColor: '#fff' }}
                            />
                            {errors.rskAssociatedFramework && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.rskAssociatedFramework}
                                </Typography>
                            )}
                        </Grid2>
                        <ViewFeeStage frameworkId={formData.rskAssociatedFramework} name={rskFrameworkOption.find(
                            (opt) => opt.value === formData.rskAssociatedFramework
                        )?.text || "RSK BSL Associated Framework"}>

                        </ViewFeeStage>
                    </Grid2>
                </Paper>

                <Grid2 container spacing={2} sx={{px: 2.5, py: 1.5, backgroundColor: '#f7faff',border: '1px solid #e0eaf6', borderRadius: '14px', }}>

                    <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                        <CustomTextField
                            label="Proposal Value"
                            name="proposalValue"
                            value={formData.proposalValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                            onChange={handleChange}
                            required={false}
                            sx={{ mb: 1, backgroundColor: '#fff'}}
                            disable
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                        <CustomTextField
                            label="Confidence Interval (%)"
                            name="confidenceInterval"
                            type="number"
                            value={formData.confidenceInterval}
                            //inputProps={{ min: 1, max: 100 }}
                            // onChange={(event) => {
                            //     const interval = Number(event.target.value) || 0;
                            //     setFormData({
                            //         ...formData,
                            //         confidenceInterval: interval,
                            //         confidenceWeightedValue: interval * formData.proposalValue,
                            //     });
                            // }}
                            onChange={(event) => {
                                const value = Number(event.target.value);
                                if (value >= 0 && value <= 100) {
                                    setFormData({
                                        ...formData,
                                        confidenceInterval: value,
                                        confidenceWeightedValue: formData.proposalValue * (value / 100),
                                    });
                                    setIsDirty(true);
                                }
                            }}
                            // required={proposalId ? true : false}
                            sx={{ mb: 1, backgroundColor: '#fff'}}
                            disable={
                                !proposalId ||
                                (() => {
                                    const instructedStage = proposalStageOption.find(
                                        (x) => x.text.toLowerCase() === "instructed"
                                    );
                                    return (
                                        instructedStage &&
                                        formData.proposalStage === instructedStage.value
                                    );
                                })()
                            }
                        />
                        {errors.confidenceInterval && (
                            <Typography className="validationMessage" role="alert">
                                {errors.confidenceInterval}
                            </Typography>
                        )}
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                        <CustomTextField
                            label="Confidence Weighted Value"
                            name="confidenceWeightedValue"
                            value={formData.confidenceWeightedValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                            onChange={handleChange}
                            sx={{ mb: 1, backgroundColor: '#fff' }}
                            disable
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                        <CustomTextField
                            label="Proposal Instructed Value"
                            name="proposalInstructedValue"
                            value={formData.proposalInstructedValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                            onChange={handleChange}
                            sx={{ mb: 1, backgroundColor: '#fff' }}
                            disable
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                        <CustomTextField
                            label="Proposal Uninstructed Value"
                            name="proposalUninstructedValue"
                            value={formData.proposalUninstructedValue.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                            onChange={handleChange}
                            sx={{ mb: 3, backgroundColor: '#fff' }}
                            disable
                        />
                    </Grid2>
                </Grid2>
            </Box>
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </form>

    );
});

export default General;